import { useCallback, useEffect, useState } from 'react';
import { query_purchased_equity_list, query_purchasable_equity_list } from '@/sub-luca-plugin/apis';
import { ICanPayItem, IUserRights, ISubjectsOfCanPayItem } from '@/sub-luca-plugin/types';

export const useInitEquityData = (params?: { lucaId: number; projectId: number }) => {
    const [userRights, setUserRights] = useState<IUserRights>();
    const [categoryTitles, setCategoryTitles] = useState<string[]>([]);
    const [canPayList, setCanPayList] = useState<Array<ICanPayItem>>();

    const transformUserRights = (data: IUserRights) => {
        const { teacherAskNum, manualCorrectNum } = data;
        let newTeacherAskNum = teacherAskNum;
        let newManualCorrectNum = manualCorrectNum;
        if (+teacherAskNum > 1000000) newTeacherAskNum = `${Math.floor(+teacherAskNum / 10000)}w`;
        if (+teacherAskNum > 10000000) newTeacherAskNum = '无限';
        if (+manualCorrectNum > 1000000) newManualCorrectNum = `${Math.floor(+manualCorrectNum / 10000)}w`;
        if (+manualCorrectNum > 10000000) newManualCorrectNum = '无限';
        return { ...data, teacherAskNum: newTeacherAskNum, manualCorrectNum: newManualCorrectNum };
    };

    const initData = async (lucaId: number, projectId: number) => {
        const [{ result: payedData }, { result: canPayData }] = await Promise.all([
            query_purchased_equity_list({ projectId }),
            query_purchasable_equity_list({ lucaId, projectId }),
        ]);
        setUserRights(transformUserRights({ ...(payedData || {}), projectId, projectName: canPayData?.projectName || '' }));
        if (canPayData) {
            const { equity, aiCorrectAuth, manualCorrectAuth } = canPayData;
            const titles = ['权益', 'Luca答疑', '名师答疑'];
            if (aiCorrectAuth) titles.push('智能批改');
            if (manualCorrectAuth) titles.push('人工批改');
            titles.push('有效期');
            const newEquity = (equity || []).map((item: any) => {
                const newItem = { ...item };
                if (!aiCorrectAuth) {
                    newItem.aiCorrectNum = undefined;
                    newItem.aiCorrectNumStr = undefined;
                    newItem.aiCorrectSubjects = undefined;
                }
                if (!manualCorrectAuth) {
                    newItem.manualCorrectNum = undefined;
                    newItem.manualCorrectNumStr = undefined;
                    newItem.manualCorrectSubjects = undefined;
                }
                return newItem;
            });
            setCategoryTitles(titles);
            setCanPayList(newEquity);
        }
    };

    const resetData = () => {
        setUserRights(undefined);
        setCategoryTitles([]);
        setCanPayList(undefined);
    };

    useEffect(() => {
        if (params) {
            initData(params.lucaId, params.projectId);
        }
    }, []);

    return { userRights, categoryTitles, canPayList, refreshData: initData, resetData };
};

type IItemType = 'single_level' | 'multi_level';

interface IApplicableData {
    itemType: IItemType;
    title: string;
    list: Array<{ id: number; name: string; list?: Array<{ id: number; name: string }> }>;
}

export const useSwitchEquity = () => {
    const [productInfo, setProductInfo] = useState<ICanPayItem>();
    const [applicableData, setApplicableData] = useState<IApplicableData[]>([]);

    const transformToApplicableDataItem = (title: string, list: ISubjectsOfCanPayItem) => {
        let itemType: IItemType = 'single_level';
        const newList = list.map(item => {
            if (item.tlSubjects?.length) {
                itemType = 'multi_level';
                return {
                    id: item.id,
                    name: item.name.length > 6 ? `${item.name.slice(0, 6)}...` : item.name,
                    list: item.tlSubjects,
                };
            }
            return { id: item.id, name: item.name, list: item.tlSubjects };
        });
        return { itemType, title, list: newList };
    };

    const switchEquity = useCallback((data: ICanPayItem) => {
        const newApplicableData: IApplicableData[] = [];
        if (data.lucaSubjects?.length) {
            newApplicableData.push(transformToApplicableDataItem('luca答疑', data.lucaSubjects));
        }
        if (data.teacherSubjects?.length) {
            newApplicableData.push(transformToApplicableDataItem('名师答疑', data.teacherSubjects));
        }
        if (data.aiCorrectSubjects?.length) {
            newApplicableData.push(transformToApplicableDataItem('智能批改', data.aiCorrectSubjects));
        }
        if (data.manualCorrectSubjects?.length) {
            newApplicableData.push(transformToApplicableDataItem('人工批改', data.manualCorrectSubjects));
        }
        setApplicableData(newApplicableData);
        setProductInfo(data);
    }, []);

    const resetData = () => {
        setProductInfo(undefined);
        setApplicableData([]);
    };

    return { productInfo, applicableData, switchEquity, resetData };
};
